import {Component, input, model, output, computed, ChangeDetectionStrategy} from "@angular/core"
import {MatSliderModule} from "@angular/material/slider"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {NumericInputComponent} from "@common/components/inputs/numeric-input/numeric-input.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-slider",
    templateUrl: "./slider.component.html",
    styleUrls: ["./slider.component.scss"],
    imports: [MatSliderModule, InputContainerComponent, NumericInputComponent],
})
export class SliderComponent {
    readonly $disabled = input(false, {alias: "disabled"})
    readonly $exponent = input(
        1, // allows for non-linear sliders when set to a value other than 1
        {alias: "exponent"},
    ) // allows for non-linear sliders when set to a value other than 1
    readonly $displayValue = input(true, {alias: "displayValue"})
    readonly $displayValuePrecision = input(2, {alias: "displayValuePrecision"})
    readonly $min = input(1, {alias: "min"})
    readonly $max = input(100, {alias: "max"})
    readonly $step = input<number | undefined>(undefined, {alias: "step"})
    readonly $value = model(50, {alias: "value"})
    readonly valueChange = output<number>()
    readonly isDragging = output<boolean>()

    readonly $valueNumber = computed(() => this.$value())

    setValueNumber(value: number) {
        const step = this.$step()
        if (step !== undefined) value = Math.round(value / step) * step
        this.$value.set(Math.max(this.$min(), Math.min(this.$max(), value)))
        this.valueChange.emit(this.$value())
    }

    readonly $sliderMin = computed(() => this.value2sliderValue(this.$min()))

    readonly $sliderMax = computed(() => this.value2sliderValue(this.$max()))

    readonly $sliderStep = computed(() => {
        const step = this.$step()
        return step !== undefined ? this.value2sliderValue(step) : (this.$sliderMax() - this.$sliderMin()) / 100
    })

    readonly $sliderValue = computed(() => this.value2sliderValue(this.$value()))

    setSliderValue(value: number) {
        this.$value.set(this.sliderValue2value(value))
        this.valueChange.emit(this.$value())
    }

    private value2sliderValue(value: number): number {
        return Math.pow(value, 1 / this.$exponent())
    }

    private sliderValue2value(sliderValue: number): number {
        return Math.pow(sliderValue, this.$exponent())
    }

    protected updateValueNumber(value: number | null | undefined): void {
        if (value === null || value === undefined) return
        this.setValueNumber(value)
    }
}
